import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth.tsx';
import { useMe } from '../lib/me.tsx';
import { signOut } from '../lib/firebase.ts';
import { Avatar } from './Avatar.tsx';

/**
 * The account menu, folded behind the user's own avatar in the corner. It keeps
 * the personal/admin actions — edit profile, commissioner tools, sign out — out
 * of the main tab row so navigation reads as one clean line of destinations.
 */
export function UserMenu() {
  const { user, mock } = useAuth();
  const me = useMe();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on an outside click or Escape — the expected manners of a menu.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const go = (to: string) => {
    setOpen(false);
    navigate(to);
  };

  const name = me?.name || 'You';

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center rounded-full ring-offset-2 ring-offset-felt focus-visible:ring-2 focus-visible:ring-brass"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
      >
        <Avatar name={name} size={32} />
      </button>

      {open && (
        <div
          role="menu"
          className="card absolute right-0 z-20 mt-2 w-48 overflow-hidden p-1"
        >
          <div className="border-b border-felt-line px-3 py-2">
            <p className="truncate font-display text-sm font-bold">{name}</p>
            {mock && <p className="eyebrow mt-0.5 text-brass">preview</p>}
          </div>

          <MenuItem onClick={() => go('/profile')}>Edit Profile</MenuItem>
          {me?.isCommissioner && <MenuItem onClick={() => go('/commissioner')}>Commish</MenuItem>}

          {user && !mock && (
            <MenuItem onClick={() => signOut()} danger>
              Sign Out
            </MenuItem>
          )}
        </div>
      )}
    </div>
  );
}

function MenuItem({
  onClick,
  danger,
  children,
}: {
  onClick: () => void;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      role="menuitem"
      onClick={onClick}
      className={`block w-full rounded-md px-3 py-2 text-left font-mono text-xs uppercase tracking-wider transition-colors hover:bg-felt ${
        danger ? 'text-chalk-dim hover:text-blood' : 'text-chalk-dim hover:text-chalk'
      }`}
    >
      {children}
    </button>
  );
}
